import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { startGoogleLogin } from "@/lib/login";
import { AuraMark, AuraWordmark } from "@/components/AuraMark";
import { Button } from "@/components/ui/button";

export default function Login() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (!loading && user) nav("/dashboard"); }, [user, loading, nav]);

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img src="https://images.pexels.com/photos/7095424/pexels-photo-7095424.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=940"
          alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(8,8,10,0.55), rgba(8,8,10,0.9))" }} />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display font-extrabold tracking-tighter text-4xl text-slate-50 leading-tight">
            Study greatness.<br />Build your own.
          </h2>
          <p className="text-slate-300 mt-4 max-w-md">The production intelligence that turns intent into session-ready MIDI.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
          <div className="flex items-center gap-2.5 mb-10"><AuraMark className="w-9 h-9" /><AuraWordmark className="text-xl" /></div>
          <h1 className="font-display font-bold text-3xl text-slate-50 tracking-tight">Enter the studio</h1>
          <p className="text-slate-400 mt-3 mb-9">Sign in with Google to access your sessions, credits and builds.</p>
          <Button data-testid="google-login-btn" onClick={startGoogleLogin}
            className="w-full h-12 rounded-full bg-white text-black hover:bg-slate-200 font-semibold text-base flex items-center gap-3">
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </Button>
          <p className="text-slate-500 text-xs mt-6 leading-relaxed">
            AI production is a paid service. After sign-in you'll choose a plan or credit pack. Admins are on a permanent complimentary Pro Studio tier. By continuing you agree to our <a href="/policies?tab=terms" className="text-gold hover:underline">Terms</a>, <a href="/policies?tab=privacy" className="text-gold hover:underline">Privacy</a> and <a href="/policies?tab=ai-data" className="text-gold hover:underline">AI &amp; Voice</a> policies.
          </p>
          <button data-testid="login-back-home" onClick={() => nav("/")} className="mt-8 text-slate-400 hover:text-slate-100 text-sm transition-colors">← Back to home</button>
        </motion.div>
      </div>
    </div>
  );
}
